import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { isFlagEnabled } from "js/utilities";
import { useState } from "react";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useTranslation, Trans } from "react-i18next";

import useAgentTopThreeFAQs from "./useAgentTopThreeFAQs";

import Accordion from "components/General/Accordion";
import ContactUsCard from "components/AgentPortal/ContactUsCard";
import ExploreOurProducts from "components/General/ExploreOurProducts";
import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import CallToActionCardWithIconOnTop from "components/General/CallToActionCardWithIconOnTop";

export default function Help({ openChatWindow, setOpenChatWindow }) {
  const { t } = useTranslation();
  const topThreeFAQs = useAgentTopThreeFAQs();
  const { user } = useSelector((s) => s.user);

  useBreadcrumb(
    user?.userId
      ? [
          { name: "Home", url: "/dashboard" },
          { name: "Help", url: "#" },
        ]
      : [
          { name: "Home", url: "/" },
          { name: "Help", url: "#" },
        ]
  );
  const [isOpenEmailUs, setIsOpenEmailUs] = useState(false);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("AgentHelp-title")}</h1>
        <p>{t("AgentHelp-subTitle")}</p>
        <hr />
      </div>

      {/* FAQs */}
      {isFlagEnabled("ENABLE_EVL_PRODUCT_INFO_ON_HELP_PAGE") ? (
        <div className="container ui-component-spacing grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2>{t("AgentHelp-FAQsTitle")}</h2>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
              <div className="flex-grow mr-8">
                <p className="text-sm">{t("AgentHelp-FAQsSubTitle")}</p>
              </div>
              <div className="flex-shrink-0 mt-4 sm:mt-0">
                <Trans
                  i18nKey="AgentHelp-FAQsViewAll"
                  components={{
                    link1: (
                      <Link to="/help/faqs" className="ui-btn-primary"></Link>
                    ),
                  }}
                ></Trans>
              </div>
            </div>
            <hr />
            <div className="grid grid-cols-1 gap-4">
              {topThreeFAQs.map((faq) => (
                <Accordion item={faq} key={`FAQ - ${JSON.stringify(faq)}`} />
              ))}
            </div>
          </div>

          <div>
            <ExploreOurProducts
              productLinks={[
                {
                  name: t("ExploreOurProducts-product-1-name"),
                  url: t("ExploreOurProducts-product-1-url"),
                },
              ]}
            />
          </div>
        </div>
      ) : (
        <div className="container ui-component-spacing">
          <h2>{t("AgentHelp-FAQsTitle")}</h2>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
            <div className="flex-grow mr-8">
              <p className="text-sm">{t("AgentHelp-FAQsSubTitle")}</p>
            </div>
            <div className="flex-shrink-0 mt-4 sm:mt-0">
              <Trans
                i18nKey="AgentHelp-FAQsViewAll"
                components={{
                  link1: (
                    <Link to="/help/faqs" className="ui-btn-primary"></Link>
                  ),
                }}
              ></Trans>
            </div>
          </div>
          <hr />
          <div className="grid grid-cols-1 gap-4">
            {topThreeFAQs.map((faq) => (
              <Accordion item={faq} key={`FAQ - ${JSON.stringify(faq)}`} />
            ))}
          </div>
        </div>
      )}

      {/* Contact Us section */}
      <div id="contact-us" className="container ui-component-spacing">
        <h2>{t("AgentHelp-contactTitle-1")}</h2>
        <p className="text-sm">{t("AgentHelp-contactSubTitle")}</p>
        <hr />
        <div className="grid gap-2 grid-cols-1 md:grid-cols-3 lg:grid-row-auto">
          <div>
            <div className="pb-2">
              <CallToActionCardWithIconOnTop
                icon="#ui-icon-chat-alt"
                action="modal"
                title={
                  <Trans
                    i18nKey={"AgentHelp-contactAction-5-title"}
                    components={{
                      span1: (
                        <span className="font-body font-bold text-xl text-black" />
                      ),
                    }}
                  />
                }
                desc={
                  <>
                    <Trans
                      i18nKey={"CallAgentServiceCard-hoo-chat"}
                      components={{
                        span1: (
                          <span className="text-sm font-bold text-black" />
                        ),
                      }}
                    />
                    <Trans
                      i18nKey={"AgentHelp-contactAction-5-description"}
                      components={{
                        break1: <br />,
                        break2: <br />,
                        span1: <span className="text-black" />,
                      }}
                    />
                  </>
                }
                ctaText={t("AgentHelp-contactAction-6-ctaText")}
                ctaSrText={t("AgentHelp-contactAction-5-ctaSrText")}
                actionMethod={() => setOpenChatWindow(!openChatWindow)}
                isActionAtTheBottom={true}
              />
            </div>
            <div>
              <CallToActionCardWithIconOnTop
                action="modal"
                actionMethod={() => setIsOpenEmailUs(true)}
                icon="#ui-icon-message"
                title={
                  <Trans
                    i18nKey={"AgentHelp-contactAction-4-new-title"}
                    components={{
                      span1: (
                        <span className="font-body font-bold text-xl text-black" />
                      ),
                    }}
                  />
                }
                desc={
                  <Trans
                    i18nKey={"AgentHelp-contactAction-4-email-description"}
                    components={{
                      span1: <span className="text-black" />,
                    }}
                  />
                }
                ctaText={t("AgentHelp-contactAction-4-new-ctaText")}
                ctaSrText={t("AgentHelp-contactAction-4-new-ctaSrText")}
                ctaUrl="#"
                isActionAtTheBottom={true}
              />
            </div>
          </div>
          <div className="grid md:col-span-2">
            <ContactUsCard />
          </div>
        </div>
      </div>

      <ContactUsDialog
        dialogState={isOpenEmailUs}
        setDialogState={setIsOpenEmailUs}
      />
    </>
  );
}
